<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AdminHeaderTabs from '@/screens/_components/admin/HeaderTabs.vue'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'
import { useBillingStore } from '@/stores/billing'
import ProfileAccountBillingService from '@/services/profile-account-billing.service'
import PlanOptionCard from '@/screens/trusted/admin/billing/PlanOptionCard.vue'
import ChangeReview from '@/screens/trusted/admin/billing/ChangeReview.vue'
import { annualSavingPercent, planChoice } from '@/helpers/billing-plans'

/**
 * Where every plan change starts: choose a plan, review what it does, confirm.
 * This page is the choosing. Picking a plan opens the review slide-in, which
 * asks the API what the change would do and makes it only when the owner
 * confirms what it says. The same three steps serve upgrades, downgrades,
 * switching between monthly and annual, cancelling, reactivating, and choosing
 * what a trial turns into.
 *
 * The billing screen's plan actions link here with `?intent=change|interval|cancel`:
 * `interval` shows the other billing length, and `cancel` goes straight to the
 * review of a cancellation.
 */
const route = useRoute()
const router = useRouter()
const billingStore = useBillingStore()
const { can } = usePermissions()

const canRead = computed(() => can(PERMISSIONS.BILLING_READ))
const canManage = computed(() => can(PERMISSIONS.BILLING_UPDATE))

// The plans the API listed, once it has.
const catalog = ref()
const catalogError = ref('')
const interval = ref('month')
// What the review is open for: `{ kind, target }`, empty until a plan is chosen.
const change = ref({})
const reviewOpen = ref(false)

const summary = computed(() => billingStore.summary)
const subscription = computed(() => summary.value?.subscription ?? null)
const scheduledChange = computed(() => summary.value?.scheduled_change ?? null)
const status = computed(() => subscription.value?.status ?? null)
const trial = computed(() => subscription.value?.trial ?? null)
const isTrial = computed(() => status.value === 'trialing')
const onHold = computed(() => !!subscription.value?.collection_paused)
const reactivating = computed(() =>
    ['suspended', 'cancelled'].includes(status.value)
)

const summaryError = computed(() =>
    summary.value ? null : billingStore.errors.summary
)
const ready = computed(() => !!summary.value && !!catalog.value)

const title = computed(() =>
    isTrial.value
        ? 'Choose what happens when your trial ends'
        : reactivating.value
          ? 'Pick a plan to come back to'
          : 'Choose a plan'
)

const intro = computed(() => {
    if (isTrial.value) {
        return 'You keep everything in your trial until it ends. Pick the plan you move to then, or start one now.'
    }
    if (reactivating.value) {
        return "You're charged today, and today becomes your billing date."
    }

    return 'Upgrades start now. Downgrades and cancelling start at the end of your billing period. You see what changes before you confirm anything.'
})

const savingPercent = computed(() =>
    catalog.value ? annualSavingPercent(catalog.value.plans) : null
)

// The Free plan first, then the paid plans the API listed, each with its price
// at the length being looked at.
const options = computed(() => {
    if (!catalog.value) return []

    const list = []
    const free = catalog.value.free

    if (free) {
        list.push({
            key: 'free',
            free: true,
            name: free.name,
            entitlements: free.entitlements,
            price: null,
            target: {
                price_ulid: free.price_ulid,
                plan_name: free.name,
                amount: 0,
                currency: free.currency,
                is_free: true,
            },
        })
    }

    for (const plan of catalog.value.plans) {
        const price = plan.prices[interval.value]

        list.push({
            key: plan.plan_version_ulid,
            free: false,
            plan_version_ulid: plan.plan_version_ulid,
            name: plan.name,
            entitlements: plan.entitlements,
            price: price
                ? {
                      ...price,
                      interval: interval.value,
                      plan_name: plan.name,
                      plan_version_ulid: plan.plan_version_ulid,
                      is_free: false,
                  }
                : null,
            target: price
                ? {
                      ...price,
                      interval: interval.value,
                      plan_name: plan.name,
                      is_free: false,
                  }
                : null,
        })
    }

    return list
})

const choices = computed(() =>
    Object.fromEntries(
        options.value.map((option) => [
            option.key,
            planChoice(option, {
                subscription: subscription.value,
                scheduledChange: scheduledChange.value,
            }),
        ])
    )
)

// A plan the account is on that isn't sold any more has no card here.
const currentPlanOffMarket = computed(() => {
    const plan = subscription.value?.plan
    if (!plan || plan.is_free || !catalog.value) return false

    return !catalog.value.plans.some(
        (p) => p.plan_version_ulid === plan.plan_version_ulid
    )
})

const canStartNow = computed(
    () =>
        isTrial.value &&
        canManage.value &&
        !onHold.value &&
        !!trial.value?.converts_to &&
        !trial.value.converts_to.is_free
)

function review(kind, target) {
    if (!canManage.value || onHold.value) return

    change.value = { kind, target }
    reviewOpen.value = true
}

function choose(option) {
    const choice = choices.value[option.key]
    if (choice.state !== 'choose') return

    review(choice.kind, option.target)
}

function startNow() {
    review('end_trial', trial.value.converts_to)
}

function closeReview() {
    reviewOpen.value = false
}

function done() {
    reviewOpen.value = false
    void router.push({ name: 'AdminBillingScreen' })
}

async function loadCatalog() {
    catalogError.value = ''

    try {
        const response = await ProfileAccountBillingService.plans()
        catalog.value = response.data
    } catch (e) {
        catalogError.value =
            e?.response?.data?.message ?? "We couldn't load the plans."
    }
}

function retry() {
    void billingStore.refreshSummary()
    void loadCatalog()
}

// The length the page opens on is the one the account is billed at, or the
// other one when the owner came to switch.
let introduced = false
function applyIntent() {
    if (introduced || !subscription.value) return
    introduced = true

    const billed =
        subscription.value.plan?.interval === 'year' ? 'year' : 'month'
    const intent = route.query.intent

    interval.value =
        intent === 'interval' ? (billed === 'year' ? 'month' : 'year') : billed

    const cancellable =
        ['active', 'past_due', 'trialing'].includes(status.value) &&
        scheduledChange.value?.kind !== 'cancel'

    if (intent === 'cancel' && cancellable) {
        review('cancel', null)
    }
}

// Only a person who can see billing gets a page; the plans and the summary are
// read when the page opens. The summary may already be there from the app
// banner, and is read again so the page never starts from a stale one.
onMounted(() => {
    if (!canRead.value) return

    void billingStore.refreshSummary()
    void loadCatalog()
})

watch(
    () => [summary.value, catalog.value],
    () => {
        if (summary.value && catalog.value) applyIntent()
    },
    { immediate: true }
)
</script>

<template>
    <main
        data-testid="plan-picker-screen"
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto bg-taupe-50"
    >
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-6 sm:pt-8 lg:pt-10 xl:max-w-7xl xl:mx-auto w-full"
        >
            <h1 id="primary-heading" class="sr-only">Admin: Choose a plan</h1>
            <div class="px-4 sm:px-6 lg:px-8 pb-12 space-y-6">
                <admin-header-tabs current="billing" />

                <zen-denied-state
                    v-if="!canRead"
                    data-testid="plans-denied"
                    title="Billing is for owners and admins"
                    message="Ask an owner or admin on your account if you need to see billing."
                />

                <template v-else>
                    <div
                        class="flex flex-wrap items-start justify-between gap-4"
                    >
                        <div class="min-w-0 max-w-2xl">
                            <h2
                                class="text-2xl font-semibold text-taupe-900"
                                data-testid="plans-title"
                            >
                                {{ title }}
                            </h2>
                            <p
                                class="mt-1 text-sm text-taupe-600"
                                data-testid="plans-intro"
                            >
                                {{ intro }}
                            </p>
                        </div>
                        <zen-button
                            variant="secondary"
                            :to="{ name: 'AdminBillingScreen' }"
                            data-testid="plans-back"
                        >
                            Back to billing
                        </zen-button>
                    </div>

                    <p
                        v-if="!canManage"
                        data-testid="plans-read-only"
                        class="rounded-lg bg-taupe-100 p-3 text-sm text-taupe-700"
                    >
                        You can see the plans here but not change yours. Ask an
                        owner or admin to make changes.
                    </p>
                    <p
                        v-else-if="onHold"
                        data-testid="plans-on-hold"
                        class="rounded-lg bg-amber-50 p-3 text-sm text-amber-800"
                    >
                        Plan changes are on hold while a payment dispute is
                        open.
                    </p>

                    <div
                        v-if="summaryError || catalogError"
                        data-testid="plans-error"
                        class="space-y-3 rounded-xl border border-red-200 bg-red-50 p-5"
                    >
                        <p class="text-sm text-red-800">
                            We couldn't load the plans.
                            {{ summaryError || catalogError }}
                        </p>
                        <zen-button
                            variant="secondary"
                            size="small"
                            data-testid="plans-retry"
                            @click="retry"
                        >
                            Try again
                        </zen-button>
                    </div>

                    <div
                        v-else-if="!ready"
                        class="grid gap-6 lg:grid-cols-3"
                        data-testid="plans-loading"
                    >
                        <zen-skeleton class="h-96 w-full" />
                        <zen-skeleton class="h-96 w-full" />
                        <zen-skeleton class="h-96 w-full" />
                    </div>

                    <template v-else>
                        <div
                            class="flex flex-wrap items-center justify-between gap-4"
                        >
                            <div
                                role="group"
                                aria-label="Billing length"
                                class="inline-flex rounded-lg bg-taupe-200 p-1 text-sm"
                                data-testid="interval-toggle"
                            >
                                <button
                                    type="button"
                                    :aria-pressed="interval === 'month'"
                                    data-testid="interval-month"
                                    class="rounded-md px-4 py-1.5 font-medium transition"
                                    :class="
                                        interval === 'month'
                                            ? 'bg-white text-taupe-900 shadow-sm'
                                            : 'text-taupe-600 hover:text-taupe-900'
                                    "
                                    @click="interval = 'month'"
                                >
                                    Monthly
                                </button>
                                <button
                                    type="button"
                                    :aria-pressed="interval === 'year'"
                                    data-testid="interval-year"
                                    class="rounded-md px-4 py-1.5 font-medium transition"
                                    :class="
                                        interval === 'year'
                                            ? 'bg-white text-taupe-900 shadow-sm'
                                            : 'text-taupe-600 hover:text-taupe-900'
                                    "
                                    @click="interval = 'year'"
                                >
                                    Annual
                                    <span
                                        v-if="savingPercent"
                                        class="ml-1 text-xs text-emerald-700"
                                        data-testid="interval-saving"
                                        >Save up to {{ savingPercent }}%</span
                                    >
                                </button>
                            </div>

                            <zen-button
                                v-if="canStartNow"
                                variant="secondary"
                                data-testid="plans-start-now"
                                @click="startNow"
                            >
                                Start {{ trial.converts_to.plan_name }} now
                            </zen-button>
                        </div>

                        <p
                            v-if="currentPlanOffMarket"
                            data-testid="plans-off-market"
                            class="rounded-lg bg-sky-50 p-3 text-sm text-sky-900"
                        >
                            You're on {{ subscription.plan.plan_name }}, which
                            isn't on sale any more. If you move to another plan,
                            it won't be there to come back to.
                        </p>

                        <div
                            class="grid gap-6 md:grid-cols-2"
                            :class="
                                options.length > 3
                                    ? 'xl:grid-cols-4'
                                    : 'lg:grid-cols-3'
                            "
                            data-testid="plan-options"
                        >
                            <plan-option-card
                                v-for="option in options"
                                :key="option.key"
                                :option="option"
                                :choice="choices[option.key]"
                                :disabled="!canManage || onHold"
                                @choose="choose(option)"
                            />
                        </div>
                    </template>
                </template>
            </div>
        </section>

        <change-review
            :is-open="reviewOpen"
            :change="change"
            @close="closeReview"
            @done="done"
        />
    </main>
</template>
